import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked
} from '@mui/icons-material';
import axios from 'axios';

/**
 * BimesterProductSelector Component
 *
 * Step 1 replacement: Select a bimester/dropCycle, then select multiple looks as products.
 * Each look becomes a product in the multi-product campaign.
 *
 * @param {string} brandId - Brand ID
 * @param {array} selectedLooks - Currently selected looks
 * @param {function} onUpdateSelection - Callback when selection changes
 * @param {string} selectedBimester - Currently selected bimester
 * @param {function} onBimesterChange - Callback when bimester changes
 */
const BimesterProductSelector = ({
  brandId,
  selectedLooks = [],
  onUpdateSelection,
  selectedBimester,
  onBimesterChange
}) => {
  const [looks, setLooks] = useState([]);
  const [bimesters, setBimesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBimesters();
  }, [brandId]);

  useEffect(() => {
    if (selectedBimester) {
      fetchLooksForBimester(selectedBimester);
    }
  }, [selectedBimester, brandId]);

  const fetchBimesters = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/brands/${brandId}/planning/looks`, {
        params: { status: 'ready' }
      });

      if (response.data.success) {
        const allLooks = response.data.looks;

        // Extract unique bimesters
        const uniqueBimesters = [...new Set(allLooks.map(l => l.dropCycle))].map(cycleId => {
          const look = allLooks.find(l => l.dropCycle === cycleId);
          return {
            id: cycleId,
            label: look.dropCycleLabel,
            season: look.dropCycleSeason,
            count: allLooks.filter(l => l.dropCycle === cycleId).length
          };
        });

        setBimesters(uniqueBimesters);
      } else {
        setError(response.data.error || 'Failed to load bimesters');
      }
    } catch (err) {
      console.error('Error fetching bimesters:', err);
      setError(err.response?.data?.error || 'Failed to load bimesters');
    } finally {
      setLoading(false);
    }
  };

  const fetchLooksForBimester = async (bimesterId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/brands/${brandId}/planning/looks`, {
        params: { dropCycleId: bimesterId, status: 'ready' }
      });

      if (response.data.success) {
        setLooks(response.data.looks);
      } else {
        setError(response.data.error || 'Failed to load looks');
      }
    } catch (err) {
      console.error('Error fetching looks:', err);
      setError(err.response?.data?.error || 'Failed to load looks');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLook = (look) => {
    const isSelected = selectedLooks.some(l => l.lookId === look.lookId);

    let updated;
    if (isSelected) {
      updated = selectedLooks.filter(l => l.lookId !== look.lookId);
    } else {
      updated = [...selectedLooks, look];
    }

    onUpdateSelection(updated);
  };

  const handleSelectAll = () => {
    onUpdateSelection(looks);
  };

  const handleDeselectAll = () => {
    onUpdateSelection([]);
  };

  if (loading && bimesters.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Bimester Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            1. Select Collection Period (Bimester)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which bimester/drop cycle this campaign will launch from.
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Bimester / Drop Cycle</InputLabel>
            <Select
              value={selectedBimester || ''}
              onChange={(e) => onBimesterChange(e.target.value)}
              label="Bimester / Drop Cycle"
            >
              {bimesters.map((bimester) => (
                <MenuItem key={bimester.id} value={bimester.id}>
                  {bimester.label} ({bimester.count} looks available)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Product Selection from Bimester */}
      {selectedBimester && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  2. Select Products from Bimester
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each look you select will become a product in this campaign.
                </Typography>
              </Box>
              <Box>
                <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : looks.length === 0 ? (
              <Alert severity="info">
                No looks available in this bimester. Create looks in Brand Planning first.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {looks.map((look) => {
                  const isSelected = selectedLooks.some(l => l.lookId === look.lookId);

                  return (
                    <Grid item xs={12} md={6} key={look.lookId}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'action.selected' : 'background.paper',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                        onClick={() => handleToggleLook(look)}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="flex-start" gap={2}>
                            <Checkbox
                              checked={isSelected}
                              icon={<RadioButtonUnchecked />}
                              checkedIcon={<CheckCircle />}
                              color="primary"
                            />
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {look.productInfo?.name || `${look.categoryLabel} #${look.sequence}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {look.categoryLabel} • {look.mode}
                              </Typography>

                              <Box sx={{ mt: 2 }}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Cost
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                      ${look.estimatedCost}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Units
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                      {look.units}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                {look.plannedDiscount && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="small"
                                      label={`${(look.plannedDiscount * 100).toFixed(0)}% planned discount`}
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {selectedLooks.length > 0 && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="subtitle2">
                  {selectedLooks.length} product{selectedLooks.length > 1 ? 's' : ''} selected
                </Typography>
                <Typography variant="body2">
                  {selectedLooks.map(l => l.productInfo?.name || l.categoryLabel).join(', ')}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BimesterProductSelector;
